import axios from 'axios';

// REACT_APP_API_BASE_URL is the source of truth. It's auto-populated from
// the project-root .env (FLASK_PORT) by frontend/scripts/sync-env.js, which
// runs as the npm `prestart`/`prebuild` hook. Do NOT hardcode a port here —
// if this fallback ever fires it means the sync script didn't run.
const FALLBACK_BASE = 'http://localhost'; // intentionally portless; will fail loudly if used.
// Access process.env.REACT_APP_* DIRECTLY so CRA/webpack DefinePlugin inlines
// the values at build time. Do NOT guard with `typeof process !== 'undefined'`
// or read `process.env` as an object: `process` is not a real global in the
// browser bundle, so that guard evaluates false at runtime, discards the
// inlined values, and silently falls back to http://localhost (which then
// mixed-content-blocks every API call on the https site).
const rawBase = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || FALLBACK_BASE;
// Strip trailing '/api' or '/api/' if present to keep baseURL as server root
const API_BASE_URL = rawBase.replace(/\/api\/?$/, '');

// Create axios instance with default config.
//
// PERF: do NOT set `withCredentials: true` and do NOT set a global
// `Content-Type` header here. Auth is JWT Bearer (Authorization header),
// not cookies — withCredentials forces the browser to send a CORS
// preflight (OPTIONS) round-trip BEFORE every actual request. With a
// remote DB and 100ms+ RTTs that doubles every call's latency.
// axios auto-sets `Content-Type: application/json` only on POST/PUT/PATCH
// when the body is an object — letting GETs stay "simple" (no preflight).
const apiClient = axios.create({
  baseURL: API_BASE_URL, // Server URL only - no /api prefix here
  timeout: 10000, // 10 second timeout per conventions
});

// Add a request interceptor to inject the token
apiClient.interceptors.request.use(
  (config) => {
    // Per-tab session isolation: token lives ONLY in sessionStorage (unique
    // per browser tab, survives refresh, dies on tab close). No localStorage
    // fallback — that would leak one tab's session into another. Two tabs =
    // two independent logins, even different entities.
    const token =
      sessionStorage.getItem('anygrc_auth_token') ||
      sessionStorage.getItem('anygrc_sa_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ─────────────────────────────────────────────────────────────────────────
// Public axios instance (Plan 04-02 / DRC-05).
// No auth interceptor, no 401 redirect handler — used by anonymous endpoints
// such as POST /api/v1/auth/set-password where the caller has no JWT yet.
// Same baseURL + withCredentials as apiClient so dev-only cookies still flow.
// ─────────────────────────────────────────────────────────────────────────
export const apiPublic = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

export interface HealthCheck {
  api: string;
  database: string;
  timestamp: string;
}

export interface ConnectionStatus {
  isConnected: boolean;
  lastChecked: string;
  error?: string;
  responseTime?: number;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  count?: number;
  error?: string;
}

// Health check function
export const checkBackendHealth = async (): Promise<HealthCheck> => {
  const response = await apiClient.get<HealthCheck>('/api/health'); // ✅ Explicit /api/ prefix
  return response.data;
};

// Connection status checker with timing
export const checkConnectionStatus = async (): Promise<ConnectionStatus> => {
  const startTime = Date.now();
  try {
    const health = await checkBackendHealth();
    const responseTime = Date.now() - startTime;

    return {
      isConnected: true,
      lastChecked: new Date().toISOString(),
      responseTime,
    };
  } catch (error: any) {
    return {
      isConnected: false,
      lastChecked: new Date().toISOString(),
      error: error.message || 'Connection failed',
    };
  }
};

// Clear API cache on logout/login
export const clearApiCache = () => {
  // Clear all localStorage keys that start with 'anygrc_' except auth-related ones
  const keysToPreserve = ['anygrc_auth_token', 'anygrc_user', 'anygrc_session_expiry', 'anygrc_user_role', 'anygrc_last_entity'];

  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('anygrc_') && !keysToPreserve.includes(key)) {
      localStorage.removeItem(key);
    }
  });

  Object.keys(sessionStorage).forEach(key => {
    if (key.startsWith('anygrc_') && !keysToPreserve.includes(key)) {
      sessionStorage.removeItem(key);
    }
  });

  // Clear axios cache by resetting default headers
  apiClient.defaults.headers.common['Cache-Control'] = 'no-cache';

  console.log('API cache cleared');
};

// Test data endpoints
export const getTestData = async () => {
  const response = await apiClient.get('/api/test-data'); // ✅ Explicit /api/ prefix
  return response.data;
};

export const createTestData = async (data: { name: string; description: string }) => {
  const response = await apiClient.post('/api/test-data', data); // ✅ Explicit /api/ prefix
  return response.data;
};

export const getPublicEntities = async () => {
  try {
    const response = await apiClient.get('/api/v1/auth/entities-public');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch public entities:', error);
    return [];
  }
};

export interface AppConfig {
  is_production: boolean;
  locked_personas: string[];
  locked_modules: string[];
  lock_message: string;
}

// Public config: which login personas AND in-app modules are locked (not in the
// subscription plan) + the message to show. The backend also enforces these
// locks (login gate + SCH/PRM router guards), so this is presentation only.
// Failure defaults to nothing locked.
export const getAppConfig = async (): Promise<AppConfig> => {
  try {
    const response = await apiClient.get('/api/v1/auth/app-config');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch app config:', error);
    return { is_production: false, locked_personas: [], locked_modules: [], lock_message: '' };
  }
};

// --- Costing Batch APIs ---

// Generic Batch API helper
const createBatchApi = (basePath: string) => ({
  getAll: async () => {
    const response = await apiClient.get(`/api/v1/cst/${basePath}`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await apiClient.post(`/api/v1/cst/${basePath}`, data);
    return response.data;
  },
  update: async (id: number, data: any) => {
    const response = await apiClient.put(`/api/v1/cst/${basePath}/${id}`, data);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await apiClient.delete(`/api/v1/cst/${basePath}/${id}`);
    return response.data;
  }
});

export const costAggregationBatchesApi = createBatchApi('cost-aggregations/batches');
export const costAllocationBatchesApi = createBatchApi('cost-allocations/batches');
export const costAbsorptionBatchesApi = createBatchApi('cost-absorptions/batches');

// --- Master Data APIs ---
export const getGLMasters = async () => {
  const response = await apiClient.get('/api/v1/cst/gl-masters');
  return response.data;
};

export const getCostCentres = async () => {
  const response = await apiClient.get('/api/v1/cst/cost-centres');
  return response.data;
};

// --- Cost Aggregation Details APIs ---

export const getCostAggregationDetails = async (batchId: number) => {
  const response = await apiClient.get(`/api/v1/cst/cost-aggregations/${batchId}/details`);
  return response.data;
};

export const getCostAggregationTransactions = async (page: number = 1, limit: number = 20) => {
  const response = await apiClient.get(`/api/v1/cst/cost-aggregations/transactions?page=${page}&limit=${limit}`);
  return response.data;
};

export const createCostAggregationGLDetail = async (data: any) => {
  const response = await apiClient.post('/api/v1/cst/cost-aggregations/gl-details', data);
  return response.data;
};

export const deleteCostAggregationGLDetail = async (id: number) => {
  const response = await apiClient.delete(`/api/v1/cst/cost-aggregations/gl-details/${id}`);
  return response.data;
};

export const createCostAggregationCCDetail = async (data: any) => {
  const response = await apiClient.post('/api/v1/cst/cost-aggregations/cc-details', data);
  return response.data;
};

export const deleteCostAggregationCCDetail = async (id: number) => {
  const response = await apiClient.delete(`/api/v1/cst/cost-aggregations/cc-details/${id}`);
  return response.data;
};

export default apiClient;